import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchProofStatuses } from '../../../redux/slices/adPortal/proofSlice';
import { Card, ButtonGroup, Button, Form, Modal } from 'react-bootstrap';
import axios from 'axios';
import '../order-tracker.scss';
import StepTracker from './StepTracker';
import { determineTypeFromUrl } from '../../../helpers/proofHandler';
import { useOrderStatus } from '../../../hooks/useOrderStatus';
import { getAgreementWithLowestStatus } from '../../../helpers/getAgreementWithLowestStatus';


const ProofReady = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { orderStatus } = useOrderStatus();

    const [activeRevisionProof, setActiveRevisionProof] = useState(null);
    const { user, initialLoginData } = useSelector((state) => state.user);
    const { statuses } = useSelector((state) => state.proofStatus);

    const advertiserCode = initialLoginData?.Account?.AdvertiserCode;
    const agreements = initialLoginData?.Agreements;

    const { agreementID: paramAgreementID } = useParams();

    // Choose agreement ID: route param (if valid) -> lowest-status agreement
    const effectiveAgreementID = useMemo(() => {
        if (paramAgreementID && paramAgreementID !== 'null') return paramAgreementID;
        const lowest = getAgreementWithLowestStatus(agreements);
        return lowest?.AgreementID || null;
    }, [paramAgreementID, agreements]);

    const [adsByStatus, setAdsByStatus] = useState({
        Waiting: [],
        Approved: [],
        Rejected: [],
    });
    const [lastApprovedRequestData, setLastApprovedRequestData] = useState(null);
    const [remainingProofs, setRemainingProofs] = useState(0);
    const [showRemainingProofsModal, setShowRemainingProofsModal] = useState(false);
    const [proofs, setProofs] = useState([]);
    const [notes, setNotes] = useState('');
    const [modalContent, setModalContent] = useState({});
    const [showSuccessModal, setShowSuccessModal] = useState(false);

    const isHtmlContent = (url) => url.includes('.html');
    const isVideoContent = (url) => url.includes('.mp4');

    const ensureHttps = (url) => (url?.startsWith('http:') ? url.replace('http:', 'https:') : url || '');

    const normalizeUrl = (url) => {
        try {
            const u = new URL(url);
            u.protocol = 'https:';
            return u.toString();
        } catch {
            return url;
        }
    };

    const getLatestStatuses = (list) => {
        return Object.values(
            (list || []).reduce((acc, status) => {
                const key = normalizeUrl(status.Url);
                if (!acc[key] || new Date(status.LastUpdated || 0) > new Date(acc[key].LastUpdated || 0)) {
                    acc[key] = status;
                }
                return acc;
            }, {})
        );
    };

    // Fetch statuses when we have an agreement id
    useEffect(() => {
        if (effectiveAgreementID) {
            dispatch(fetchProofStatuses(String(effectiveAgreementID)));
        }
    }, [effectiveAgreementID, dispatch]);

    // Build proofs once agreements load
    useEffect(() => {
        const addProofs = [];
        const urlsSet = new Set();

        agreements?.forEach((agreement) => {
            agreement.Contracts?.forEach((contract) => {
                const types = [
                    { key: 'LocalMockupUrlNS', defaultType: 'Local Mockup NS' },
                    { key: 'LocalMockupUrlSS', defaultType: 'Local Mockup SS' },
                    { key: 'SyndicatedUrl', defaultType: 'Syndicated Ad' },
                    { key: 'MobileUrl', defaultType: 'Mobile Ad' },
                ];
                types.forEach(({ key, defaultType }) => {
                    const urlObject = contract[key];
                    if (urlObject?.Url && !urlsSet.has(urlObject.Url)) {
                        const type = determineTypeFromUrl(urlObject.Url, defaultType);
                        addProofs.push({
                            type,
                            url: ensureHttps(urlObject.Url),
                            contractNumber: contract.ContractNumber,
                            lastUpdated: urlObject.TimeStamp,
                        });
                        urlsSet.add(urlObject.Url);
                    }
                });
            });
        });

        setProofs(addProofs);
    }, [agreements]);

    // Categorize proofs by status (normalize URLs on both sides)
    useEffect(() => {
        const uniqueStatuses = getLatestStatuses(statuses);
        const next = { Waiting: [], Approved: [], Rejected: [] };

        proofs?.forEach((proof) => {
            const proofUrl = normalizeUrl(proof.url);
            const match = uniqueStatuses.find((s) => normalizeUrl(s.Url) === proofUrl);
            if (match && ['Approved', 'Rejected', 'Waiting'].includes(match.Status)) {
                if (!next[match.Status].some((ad) => normalizeUrl(ad.url) === proofUrl)) {
                    next[match.Status].push({ ...proof, status: match.Status, notes: match.Notes || '' });
                }
            } else {
                next.Waiting.push(proof);
            }
        });

        setAdsByStatus(next);
    }, [proofs, statuses]);

    useEffect(() => {
        setRemainingProofs(adsByStatus.Waiting.length);
    }, [adsByStatus]);

    const getFileNameWithQueryParams = (url) => {
        try {
            const parsedUrl = new URL(url);
            const pathname = parsedUrl.pathname;
            let fileName = pathname.split('/').pop();
            const qs = parsedUrl.search.slice(1);
            if (qs) {
                const queryParams = qs.split('&').map((param) => param.replace(/=/g, '-').replace(/\?/g, '_')).join('_');
                fileName += `_${queryParams}`;
            }
            return fileName || 'default_filename';
        } catch {
            return 'default_filename';
        }
    };

    const submitAdTrackerUpdate = async (jsonObj, typeID) => {
        const token = localStorage.getItem('token');
        const idNum = Number.parseInt(effectiveAgreementID, 10);

        if (!Number.isInteger(idNum) || idNum <= 0) {
            alert('There was an issue with your session. Please log out and back in.');
            console.error('Invalid agreementID during revision:', { effectiveAgreementID });
            return;
        }

        const workQueueItemData = {
            TypeID: typeID,
            AgreementID: idNum,
            UserID: user.UserID,
            Data: JSON.stringify(jsonObj),
        };

        try {
            await axios.post(`${process.env.REACT_APP_BASE_URL}/api/CRM/SubmitAdTrackerUpdate`, workQueueItemData, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setNotes('');
            setActiveRevisionProof(false);
            console.log('Ad Tracker Update submitted successfully');
        } catch (error) {
            console.error('Error submitting Ad Tracker Update:', error);
        }
    };

    const handleProofApproval = async ({ notes, url, contractNumber, lastUpdated }) => {
        const token = localStorage.getItem('token');
        const fileName = getFileNameWithQueryParams(url);

        const requestData = {
            directory: '',
            file: fileName,
            notes,
            status: 'Approved',
            advertiserCode,
            contractNumber,
            url,
            fileName: `${fileName}.png`,
            lastUpdated,
            userID: user.UserID,
            agreementID: effectiveAgreementID,
        };

        try {
            await axios.post(`${process.env.REACT_APP_BASE_URL}/api/AdPortalUpload/ApproveProof`, requestData, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setLastApprovedRequestData(requestData);
            dispatch(fetchProofStatuses(String(effectiveAgreementID)));
        } catch (error) {
            console.error('Error in handleProofApproval:', error);
        }

        const remaining = remainingProofs - 1;
        setRemainingProofs(remaining);
        setModalContent({
            title: 'Proof Approved',
            message: 'Your proof has been successfully approved!',
            remainingMessage:
                remaining && remaining > 0 ? `You have ${remaining} ${remaining === 1 ? 'proof' : 'proofs'} left to approve or revise.` : null,
        });
        setShowSuccessModal(true);
    };

    useEffect(() => {
        const uniqueStatuses = getLatestStatuses(statuses);
        const allApproved = uniqueStatuses.length > 0 && uniqueStatuses.every((s) => s.Status === 'Approved');
        const statusID = orderStatus === 10 ? 20 : 14;
        if (allApproved && lastApprovedRequestData) {
            submitAdTrackerUpdate(lastApprovedRequestData, statusID)
                .then(() => setLastApprovedRequestData(null))
                .catch(console.error);
        }
    }, [statuses, lastApprovedRequestData]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleInputChange = (e) => setNotes(e.target.value);

    const handleRevisionRequestToggle = (proofUrl = null) => {
        setActiveRevisionProof(proofUrl);
        setNotes('');
    };

    const handleRevisionRequest = async ({ url, contractNumber, lastUpdated }) => {
        const statusID = orderStatus === 10 ? 17 : 11;
        const token = localStorage.getItem('token');

        if (!url) {
            console.warn('No URL provided for revision request');
            return;
        }

        const fileName = getFileNameWithQueryParams(url);

        const requestData = {
            directory: `\\\\sweb5\\ClubCom\\Websites\\AdPortalContent\\${advertiserCode}\\Proofs\\Rejected`,
            file: fileName,
            notes,
            status: 0,
            advertiserCode,
            contractNumber,
            url,
            fileName: `${fileName}.png`,
            lastUpdated,
            userID: user.UserID,
            agreementID: effectiveAgreementID,
        };

        try {
            await axios.post(`${process.env.REACT_APP_BASE_URL}/api/AdPortalUpload/RevisionRequest`, requestData, {
                headers: { Authorization: `Bearer ${token}` },
            });
            await submitAdTrackerUpdate(requestData, statusID);
            await dispatch(fetchProofStatuses(String(effectiveAgreementID)));

            const remaining = remainingProofs - 1;
            setRemainingProofs(remaining);
            setModalContent({
                title: 'Revision Requested',
                message: 'Your revision request has been successfully submitted!',
                remainingMessage:
                    remaining && remaining > 0 ? `You have ${remaining} ${remaining === 1 ? 'proof' : 'proofs'} left to approve or revise.` : null,
            });
            setShowSuccessModal(true);
        } catch (error) {
            console.error('Error in handleRevisionRequest:', error);
            setModalContent({
                title: 'Error',
                message: 'Failed to submit revision request. Please try again or contact support if the problem persists.',
                remainingMessage: null,
            });
            setShowSuccessModal(true);
        }
    };
    const typeCounters = {}; // if you increment these somewhere else, keep; otherwise remove

    const renderAds = (ads, status) => (
        <>
            {ads.length > 0 && (
                <Card.Header className="proof-ready-header text-end">
                    {status.includes('Waiting') ? `${status} for Approval` : status}
                </Card.Header>
            )}
            {ads.map((ad) => {
                const isRevisionFormOpen = activeRevisionProof === ad.url;
                return (
                    <Card
className="border-1 p-1 my-3 mx-1"
key={ad.url}
                    >
                        <Card.Body className="p-1">
                            <div className={`${status}-tag d-flex justify-content-between align-items-center w-100`}>
                                {`${ad.type} ${typeCounters[ad.type] >= 1 ? typeCounters[ad.type] : ''}`}
                            </div>
                            {ad.notes && <div>Rejected reason: <i>{ad.notes}</i></div>}
                            {isHtmlContent(ad.url) ? (
                                <iframe
className="text-center p-2"
src={ad.url}
title={ad.type}
                                />
                            ) : isVideoContent(ad.url) ? (
                                <video
className="w-75 text-center p-4"
controls
src={ad.url}
                                >
                                    Your browser does not support the video tag.
                                </video>
                            ) : (
                                <img
alt="Ad Content"
className="w-100 p-2"
src={ad.url}
                                />
                            )}

                            {!status.includes('Rejected') && !status.includes('Approved') && (
                                <>
                                    <ButtonGroup className="w-100">
                                        <Button onClick={() => handleProofApproval(ad)}>Approve Changes</Button>
                                        <Button onClick={() => handleRevisionRequestToggle(ad.url)}>Request Revision</Button>
                                    </ButtonGroup>
                                    {isRevisionFormOpen && (
                                        <Form className="mt-3">
                                            <Form.Group controlId="revisionNotes">
                                                <Form.Label>Notes:</Form.Label>
                                                <Form.Control
                                                    as="textarea"
                                                    className="w-100 rounded-1"
                                                    onChange={handleInputChange}
                                                    rows={3}
                                                    value={notes}
                                                />
                                            </Form.Group>
                                            <ButtonGroup className="w-100 mt-2">
                                                <Button
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        handleRevisionRequest({
                                                            notes,
                                                            url: ad.url,
                                                            contractNumber: ad.contractNumber,
                                                            lastUpdated: ad.lastUpdated,
                                                        });
                                                    }}
                                                    type="submit"
                                                    variant="primary"
                                                >
                                                    Submit Revision Request
                                                </Button>
                                                <Button
onClick={() => handleRevisionRequestToggle()}
variant="secondary"
                                                >
                                                    Cancel
                                                </Button>
                                            </ButtonGroup>
                                        </Form>
                                    )}
                                </>
                            )}
                        </Card.Body>
                    </Card>
                );
            })}
        </>
    );

    return (
        <>
            <div className="status-bg">
                <StepTracker />
                <Card className="border-0 status-description">
                    <Card.Body className="pt-0">
                        <Card.Title className="step-title text-center d-flex justify-content-center align-items-center">
                            Proofs Ready
                        </Card.Title>
                        <Card.Text>Your ads are now online for approval!</Card.Text>

                        <Modal
onHide={() => setShowSuccessModal(false)}
show={showSuccessModal}
                        >
                            <Modal.Header closeButton>
                                <Modal.Title>{modalContent.title}</Modal.Title>
                            </Modal.Header>
                            <Modal.Body>
                                <p>{modalContent.message}</p>
                                {modalContent.remainingMessage && <p>{modalContent.remainingMessage}</p>}
                            </Modal.Body>
                            <Modal.Footer>
                                <Button
onClick={() => setShowSuccessModal(false)}
variant="secondary"
                                >
                                    Close
                                </Button>
                                {remainingProofs === 0 && modalContent.title !== 'Error' && (
                                        <Button
onClick={() => navigate('/ad-account')}
variant="primary"
                                        >
                                            Exit
                                        </Button>
                                    )}
                                )}
                            </Modal.Footer>
                        </Modal>
                    </Card.Body>
                </Card>
            </div>

            {renderAds(adsByStatus.Waiting, 'Waiting')}
            {renderAds(adsByStatus.Approved, 'Approved')}
            {renderAds(adsByStatus.Rejected, 'Rejected')}
        </>
    );
};

export default ProofReady;
